import React from "react";
import { GQLOffers, GQLUrls } from "@gql/types/gql";
import {
    AccordionData,
    ICustomDomain,
    IVendorComparePageProps,
} from "@genericTypes/sharedTypes";
import MetaTags from "@components/shared/metaTags";
import { GetStaticPropsContext } from "next";
import { translate } from "@helpers/translationObjectFormatter";
import { TranslationsObject } from "@genericTypes/sharedTypes";
import { useQuery } from "@tanstack/react-query";
import Breadcrumb from "@components/shared/breadcrumb";
import ContentTab from "@components/shared/contentTab";
import { Session } from "src/api/session";
import initialCall, { initialCallPaths } from "@helpers/initial-call";
import { replaceDkis, resolveURL, titleDecoder } from "@lib/sharedUtils";
import Accordion from "@components/shared/newAccordion";
import Compare from "@components/vendor/compare";

const VendorCompare = () => {
    const session = useQuery<Session>({ queryKey: ["session"] });
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const { data: props } = useQuery<IVendorComparePageProps>({
        queryKey: ["vendorComparePageProps"],
    });
    const { offer, category, dkis } = props ?? {};
    let arr;
    if (category && (category?.label || category?.name)) {
        arr = [
            {
                label: translate(
                    translations,
                    "several_general_breadcrumb_home",
                ),
                link: "/",
                isActive: true,
                showOnMobile: true,
            },
            {
                label: `${(category?.label as string) ?? category?.name}`,
                link: `${
                    resolveURL(category?.url as GQLUrls) ??
                    `/${category?.slug as string}`
                }`,
                isActive: true,
                showOnMobile: true,
            },
            {
                label: replaceDkis(offer?.url?.url as string, dkis),
                link: "#",
                isActive: false,
                showOnMobile: false,
            },
        ];
    } else {
        arr = [
            {
                label: translate(
                    translations,
                    "several_general_breadcrumb_home",
                ),
                link: "/",
                isActive: true,
                showOnMobile: true,
            },
            {
                label: replaceDkis(offer?.url?.url as string, dkis),
                link: "#",
                isActive: false,
                showOnMobile: false,
            },
        ];
    }

    const arrTab = [
        {
            label: translate(translations, "several_general_reviewWord"),
            slug: "review",
            link: `${resolveURL(offer?.url as GQLUrls)}`,
        },
        {
            label: translate(translations, "several_general_compareButton"),
            slug: "compare",
            link: `${resolveURL(offer?.url as GQLUrls)}/compare`,
        },
    ];
    const { data: domainInfo } = useQuery<ICustomDomain, Error>({
        queryKey: ["domain"],
    });
    const getKeyWords = () => {
        for (let i = 0; i < (offer?.metatags?.length ?? 0); i++) {
            if (
                offer?.metatags![i]?.key === "keywords" &&
                offer?.metatags[i]?.value
            ) {
                return offer?.metatags[i]?.value;
            }
        }
        for (let i = 0; i < (domainInfo?.domain?.metatags?.length ?? 0); i++) {
            if (
                domainInfo?.domain?.metatags![i]?.key === "keywords" &&
                domainInfo?.domain?.metatags[i]?.value
            ) {
                return domainInfo?.domain?.metatags[i]?.value;
            }
        }

        return "vpn , auto warranty";
    };
    const getDescription = (fallback: string) => {
        for (let i = 0; i < (offer?.metatags?.length ?? 0); i++) {
            if (
                offer?.metatags![i]?.key === "description" &&
                offer?.metatags[i]?.value
            ) {
                return offer?.metatags[i]?.value;
            }
        }
        if (offer?.overview) {
            return offer.overview.replace(/<[^>]+>/g, "").substring(0, 160);
        }

        for (let i = 0; i < (domainInfo?.domain?.metatags?.length ?? 0); i++) {
            if (
                domainInfo?.domain?.metatags![i]?.key === "description" &&
                domainInfo?.domain?.metatags[i]?.value
            ) {
                return domainInfo?.domain?.metatags[i]?.value;
            }
        }
        return fallback;
    };

    const FAQs = offer?.faqs?.map((item) => ({
        id: item?.id as number,
        title: item?.question as string,
        content: item?.answer as string,
    })) as AccordionData[];

    return (
        <>
            <MetaTags
                title={`${translate(
                    translations,
                    "several_comparePage_meta-title-1",
                )} ${offer?.name as string} ${translate(
                    translations,
                    "several_comparePage_meta-title-2",
                )} | Several.com`}
                image={
                    (offer?.mainImage?.fullPath as string) ??
                    "public/images25.png"
                }
                keyWords={getKeyWords() as string}
                description={
                    getDescription(
                        translate(
                            translations,
                            "several_general_meta_description",
                        ),
                    ) as string
                }
                url={`${resolveURL(offer?.url as GQLUrls)}/compare`}
            />
            <div className="max-w-7xl mx-auto px-5">
                <Breadcrumb arr={arr} />
                <div className="flex justify-between items-baseline mt-4 flex-col lg:flex-row  lg:ml-0">
                    <h2 className="lg:text-4xl text-2xl lg:leading-9 font-bold  max-w-[744px]">
                        {titleDecoder(offer?.name ?? "", {
                            category: category
                                ? ((category.label ?? category.name) as string)
                                : "",
                            region:
                                (session?.data?.visitDetails
                                    ?.region as string) ?? "",
                        })}
                    </h2>
                </div>
                <div className="mt-10">
                    <ContentTab contentTab={arrTab} />
                </div>
                <Compare
                    offersToCompare={[offer as GQLOffers]}
                    title={`Compare between ${
                        offer?.name as string
                    } and other vendors`}
                />
                {(offer?.faqs?.length as number) > 0 && (
                    <div className="mb-48">
                        <div className="mt-24" id="faq">
                            <Accordion title="FAQs" data={FAQs} />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export const getStaticProps = async (ctx: GetStaticPropsContext) => {
    return await initialCall("VendorCompareById", ctx);
};

export function getStaticPaths() {
    return initialCallPaths();
}

export default VendorCompare;
